<template>
    <div class="selector-wrapper">
        <primevue-select
            v-model="selectedTimeZoneId"
            :input-id="props.inputId"
            option-value="id"
            option-label="name"
            :disabled="props.disabled"
            :options="timeZoneOptions"
            placeholder="Select timezone"
            filter
        />
    </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import PrimevueSelect from 'primevue/select';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { findIana } from 'windows-iana';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';

const dataStore = useDataStore();

dayjs.extend(tz);

const TIMEZONE_GROUP_ORDER: Array<string> = [
    'UTC',
    'Europe',
    'Africa',
    'America',
    'Asia',
    'Australia',
    'Atlantic',
    'Pacific',
    'Indian',
    'Antarctica',
    'Other',
];

const props = defineProps<{
    modelValue: IIdName;
    disabled?: boolean;
    displayById?: boolean;
    iana?: boolean;
    showUtcOffset?: boolean;
    inputId?: string;
}>();

const emit = defineEmits<{
    'update:modelValue': [value: IIdName];
}>();

const selectedTimeZoneId = ref<string>(props.modelValue.id);
const timeZoneOptions = ref<Array<IIdName>>([]);

watch(() => selectedTimeZoneId.value, (newVal) => {
    if (!timeZoneOptions.value.length) return;
    const timeZone = timeZoneOptions.value.find(x => x.id === newVal);

    if (!timeZone) return;
    emit('update:modelValue', timeZone);
});

function removeDuplicates (options: Array<IIdName>): Array<IIdName> {
    const seen = new Set<string>();
    return options.filter(option => {
        if (seen.has(option.id)) return false;
        seen.add(option.id);
        return true;
    });
}

function orderTimeZones (options: Array<IIdName>): Array<IIdName> {
    const groups: Record<string, Array<IIdName>> = {};
    TIMEZONE_GROUP_ORDER.forEach((group) => {
        groups[group] = [];
    });

    function classify (timeZoneId: string): keyof typeof groups {
        const id = (timeZoneId ?? '').trim();
        if (id.toUpperCase() === 'UTC') return 'UTC';
        const prefix = id.split('/')[0];
        if (Object.keys(groups).includes(prefix)) return prefix;
        return 'Other';
    }

    options.forEach((option) => {
        groups[classify(option.id)].push(option);
    });

    return TIMEZONE_GROUP_ORDER.flatMap(group => groups[group]);
}

function mapTimezoneInfo (timezoneInfo: Array<IIdName>): Array<IIdName> {
    return timezoneInfo.flatMap(timezone => {
        const ianaTimezones = findIana(timezone.id);
        const name = props.displayById ? timezone.id : timezone.name;
        const offset = props.showUtcOffset && ianaTimezones?.length ? ` (${dayjs.tz(undefined, ianaTimezones[0]).format('Z')})` : '';

        if (!ianaTimezones?.length) {
            return {
                id: timezone.id,
                name: `${name}${offset}`,
            };
        }

        if (timezone.id.includes('UTC')) {
            return {
                id: timezone.id,
                name,
            };
        }

        if (props.iana) {
            return ianaTimezones.map(ianaTimezone => ({
                id: ianaTimezone,
                name: `${ianaTimezone}${offset}`,
            }));
        }

        return {
            id: timezone.id,
            name: `${name}${offset}`,
        };
    });
}

function initialise (): void {
    dataStore.getTimezones().then((response) => {
        if (!response) return;

        const mapped = mapTimezoneInfo(response.timeZoneInfo);
        timeZoneOptions.value = removeDuplicates(orderTimeZones(mapped));

        if (!props.modelValue.id) {
            selectedTimeZoneId.value = response.defaultTimeZone.id;
        }
    });
}

initialise();

watch(() => props.modelValue, (newVal) => {
    if (newVal.id !== selectedTimeZoneId.value) {
        selectedTimeZoneId.value = newVal.id;
    }
});
</script>
